// shared/components/PolicyPanel.jsx
import React from "react";
import { getAllCapabilities } from "../../lib/logicalCanvas/pluginRegistry.js";

export default function PolicyPanel({ effectivePolicy }) {
  const capabilities = getAllCapabilities();

  const policyEntries = Array.from(capabilities.entries()).map(([capId, capability]) => {
    const policy = effectivePolicy[capId] || {
      enabled: false,
      mode: capability.accessDefault,
      locked: false,
      source: "default",
    };

    return {
      capId,
      label: capability.label,
      systemName: capability.systemName,
      policy,
    };
  });

  // Group by system
  const grouped = policyEntries.reduce((acc, entry) => {
    const system = entry.systemName;
    if (!acc[system]) acc[system] = [];
    acc[system].push(entry);
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      <div className="text-mist/90 text-sm">
        This panel shows the resolved effective policy for all capabilities. Policies are
        resolved from upstream governance, local user preferences, and registry defaults.
      </div>

      {Object.entries(grouped).map(([systemName, entries]) => (
        <div key={systemName} className="surface rounded p-4">
          <h4 className="text-ice font-semibold mb-3">{systemName}</h4>
          <div className="space-y-2">
            {entries.map(({ capId, label, policy }) => (
              <div
                key={capId}
                className="flex items-center justify-between text-sm py-2 border-b border-edge/30 last:border-0"
              >
                <div className="flex items-center gap-2">
                  <span className={policy.enabled ? "text-ice" : "text-mist/60"}>
                    {label}
                  </span>
                  {policy.locked && (
                    <svg className="h-3 w-3 text-mist/50" viewBox="0 0 16 16" fill="currentColor">
                      <path d="M8 1a3 3 0 0 0-3 3v2H4a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V7a1 1 0 0 0-1-1h-1V4a3 3 0 0 0-3-3zm1 5V4a1 1 0 1 0-2 0v2h2z"/>
                    </svg>
                  )}
                </div>
                <div className="flex items-center gap-3">
                  <span
                    className={`text-xs px-2 py-1 rounded ${
                      policy.enabled ? "bg-[#1f3d29] text-[#7fd199]" : "bg-[#3d2020] text-[#d19999]"
                    }`}
                  >
                    {policy.enabled ? "Enabled" : "Disabled"}
                  </span>
                  <span
                    className={`text-xs px-2 py-1 rounded ${
                      policy.mode === "read-write"
                        ? "bg-[#1f3d29] text-[#7fd199]"
                        : policy.mode === "read-only"
                        ? "bg-[#2d3540] text-[#9db4ce]"
                        : "bg-[#3d2020] text-[#d19999]"
                    }`}
                  >
                    {policy.mode}
                  </span>
                  <span className="text-xs text-mist/60 w-16 text-right">
                    {policy.source}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
